/**
 * useViewProps Hook
 * Phase 1 Days 46-48 - Prop assembly extraction
 *
 * Assembles all props for DeviceManagementView and PatronManagementView.
 * This reduces the size of AppContent.js render section significantly.
 */

import { useMemo } from "react";
import { CredentialsSection } from "../components/CredentialManagement";

export function useViewProps({
  // Theme and UI
  theme,
  activeLogo,
  toggleLogo,

  // Organization context
  organizationId,
  organizationName,
  installationId,
  installationName,
  orgInstallContext,
  orgSettings,

  // Navigation
  navigateToPatrons,
  navigateToDevices,
  openAdvancedSettings,
  handleOpenInstallationActions,
  handleOpenInstallationManagement,
  handleOpenOrganizationManagement,
  onToggleDevStreams,

  // Device data
  devices,
  selectedDeviceId,
  setSelectedDeviceId,
  deviceStates,
  deviceViewMode,
  handleViewModeChange,
  allDeviceStatesLoaded,

  // Device actions
  handleRefresh,
  isRefreshing,
  setIsDeviceClaimingModalOpen,
  handleDeviceAction,
  handleDownloadSettings,
  handleUpdateFirmware,
  isDownloadingSettings,
  isSyncing,
  loadingAction,

  // Display name editing
  handleEditDisplayNameClick,
  handleEditCredentialsSettingClick,

  // Device state
  connectivityStatusUpdateTimes,
  actionHistory,
  actionHistoryLimit,
  setActionHistoryLimit,

  // Collapse states
  isDeviceStateCollapsed,
  isDeviceActionsCollapsed,
  isLastActionCollapsed,
  toggleDeviceStateCollapsed,
  toggleDeviceActionsCollapsed,
  toggleLastActionCollapsed,
  isCredentialGridCollapsed,
  toggleCredentialGridCollapsed,
  isDeviceSettingsCollapsed,
  toggleDeviceSettingsCollapsed,

  // Component ordering
  components,
  handleOnDragEnd,

  // Credentials
  credentials,
  credentialsLoaded,
  patronsMap,
  pendingDeletionCredentials,
  offlineDeviceCredentials,
  devicesWithPendingDeletions,
  handleDeleteCredentialFromDevice,

  // Patron data
  pendingDeletionPatrons,
  setPendingDeletionPatrons,
  setPendingDeletionCredentials,
  pendingAdditionCredentials,
  setPendingAdditionCredentials,
  pendingFullCredentialDeletions,
  setPendingFullCredentialDeletions,

  // Events
  devStreamEvents,
  lastCredentialDownload,
  lastCredentialProvisioned,
  lastCredentialRemoved,
}) {
  const patronViewProps = useMemo(
    () => ({
      theme,
      activeLogo,
      onToggleLogo: toggleLogo,
      organizationId,
      organizationName,
      installationId,
      installationName,
      onNavigateToPatrons: navigateToPatrons,
      onNavigateToDevices: navigateToDevices,
      onOpenAdvancedSettings: openAdvancedSettings,
      onOpenInstallationActions: handleOpenInstallationActions,
      onOpenInstallationManagement: handleOpenInstallationManagement,
      onOpenOrganizationManagement: handleOpenOrganizationManagement,
      onToggleDevStreams,
      deviceCount: devices?.length || 0,
      devices: devices || [],
      onDevicesClaimed: handleRefresh,
      allDeviceStatesLoaded,
      currentOrgSettings: orgSettings,
      orgInstallContext,
      organizations: orgInstallContext?.organizations || [],
      pendingDeletionPatrons,
      setPendingDeletionPatrons,
      pendingDeletionCredentials,
      setPendingDeletionCredentials,
      pendingAdditionCredentials,
      setPendingAdditionCredentials,
      pendingFullCredentialDeletions,
      setPendingFullCredentialDeletions,
      devStreamEvents,
      lastCredentialDownload,
      lastCredentialProvisioned,
      lastCredentialRemoved,
    }),
    [
      theme,
      activeLogo,
      toggleLogo,
      organizationId,
      organizationName,
      installationId,
      installationName,
      navigateToPatrons,
      navigateToDevices,
      openAdvancedSettings,
      handleOpenInstallationActions,
      handleOpenInstallationManagement,
      handleOpenOrganizationManagement,
      onToggleDevStreams,
      devices,
      handleRefresh,
      allDeviceStatesLoaded,
      orgSettings,
      orgInstallContext,
      pendingDeletionPatrons,
      setPendingDeletionPatrons,
      pendingDeletionCredentials,
      setPendingDeletionCredentials,
      pendingAdditionCredentials,
      setPendingAdditionCredentials,
      pendingFullCredentialDeletions,
      setPendingFullCredentialDeletions,
      devStreamEvents,
      lastCredentialDownload,
      lastCredentialProvisioned,
      lastCredentialRemoved,
    ]
  );

  const deviceViewProps = useMemo(
    () => ({
      theme,
      activeLogo,
      onToggleLogo: toggleLogo,
      organizationId,
      organizationName,
      installationId,
      installationName,
      onNavigateToPatrons: navigateToPatrons,
      onOpenAdvancedSettings: openAdvancedSettings,
      onOpenInstallationActions: handleOpenInstallationActions,
      onOpenInstallationManagement: handleOpenInstallationManagement,
      onOpenOrganizationManagement: handleOpenOrganizationManagement,
      onToggleDevStreams,
      deviceCount: devices?.length || 0,
      allDeviceStatesLoaded,
      currentOrgSettings: orgSettings,
      orgInstallContext,
      devices,
      selectedDeviceId,
      onDeviceSelect: setSelectedDeviceId,
      onRefresh: handleRefresh,
      isRefreshing,
      onAddDevice: () => setIsDeviceClaimingModalOpen(true),
      deviceViewMode,
      onViewChange: handleViewModeChange,
      deviceStates,
      actionHistory,
      actionHistoryLimit,
      onActionHistoryLimitChange: setActionHistoryLimit,
      loadingAction,
      onExecuteAction: handleDeviceAction,
      onDownloadSettings: handleDownloadSettings,
      onUpdateFirmware: handleUpdateFirmware,
      isDownloadingSettings,
      isSyncing,
      onEditDisplayNameClick: handleEditDisplayNameClick,
      onEditCredentialsSettingClick: handleEditCredentialsSettingClick,
      connectivityStatusUpdateTime:
        connectivityStatusUpdateTimes[selectedDeviceId],
      collapseStates: {
        deviceState: isDeviceStateCollapsed,
        deviceActions: isDeviceActionsCollapsed,
        lastAction: isLastActionCollapsed,
        deviceSettings: isDeviceSettingsCollapsed,
      },
      onToggleCollapse: {
        deviceState: toggleDeviceStateCollapsed,
        deviceActions: toggleDeviceActionsCollapsed,
        lastAction: toggleLastActionCollapsed,
        deviceSettings: toggleDeviceSettingsCollapsed,
      },
      components,
      onDragEnd: handleOnDragEnd,
      credentialsSection:
        deviceViewMode === "detailed" ? (
          <CredentialsSection
            credentials={credentials}
            credentialsLoaded={credentialsLoaded}
            isCollapsed={isCredentialGridCollapsed}
            onToggleCollapse={toggleCredentialGridCollapsed}
            selectedDeviceId={selectedDeviceId}
            patronsMap={patronsMap}
            pendingDeletionCredentials={pendingDeletionCredentials}
            offlineDeviceCredentials={offlineDeviceCredentials}
            devicesWithPendingDeletions={devicesWithPendingDeletions}
            onDeleteCredential={handleDeleteCredentialFromDevice}
          />
        ) : null,
    }),
    [
      theme,
      activeLogo,
      toggleLogo,
      organizationId,
      organizationName,
      installationId,
      installationName,
      navigateToPatrons,
      openAdvancedSettings,
      handleOpenInstallationActions,
      handleOpenInstallationManagement,
      handleOpenOrganizationManagement,
      onToggleDevStreams,
      devices,
      allDeviceStatesLoaded,
      orgSettings,
      orgInstallContext,
      selectedDeviceId,
      setSelectedDeviceId,
      handleRefresh,
      isRefreshing,
      setIsDeviceClaimingModalOpen,
      deviceViewMode,
      handleViewModeChange,
      deviceStates,
      actionHistory,
      actionHistoryLimit,
      setActionHistoryLimit,
      loadingAction,
      handleDeviceAction,
      handleDownloadSettings,
      handleUpdateFirmware,
      isDownloadingSettings,
      isSyncing,
      handleEditDisplayNameClick,
      handleEditCredentialsSettingClick,
      connectivityStatusUpdateTimes,
      isDeviceStateCollapsed,
      isDeviceActionsCollapsed,
      isLastActionCollapsed,
      toggleDeviceStateCollapsed,
      toggleDeviceActionsCollapsed,
      toggleLastActionCollapsed,
      isDeviceSettingsCollapsed,
      toggleDeviceSettingsCollapsed,
      components,
      handleOnDragEnd,
      credentials,
      credentialsLoaded,
      isCredentialGridCollapsed,
      toggleCredentialGridCollapsed,
      patronsMap,
      pendingDeletionCredentials,
      offlineDeviceCredentials,
      devicesWithPendingDeletions,
      handleDeleteCredentialFromDevice,
    ]
  );

  return {
    patronViewProps,
    deviceViewProps,
  };
}
